import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { portalColors } from "./BusinessPortalComponents";
import ArrowDownSignToNavigate from "src/img/arrow-down-sign-to-navigate.svg";

const SelectWrap = styled.div`
    position: relative;
    width: 100%;
    font-family: Manrope, Helvetica, sans-serif;
    margin-bottom: 30px;
`;

const SelectButton = styled.button`
    width: 100%;
    min-height: ${p => p.$height || "50px"};
    height: ${p => p.$height || "50px"};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: ${p => p.$padding || "0 0.85rem 0 1.1rem"};
    border: 1px solid ${p => p.$hasError ? "#FF3B3A" : p.$disabled ? portalColors.borderStrong : "#E9EBEF"};
    border-radius: ${p => p.$radius || "12px"};
    background: ${p => p.$disabled ? portalColors.surfaceSoft : portalColors.surface};
    box-shadow: none;
    color: ${portalColors.text};
    cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: ${p => p.$fontSize || "15px"};
    font-weight: ${p => p.$disabled ? 700 : p.$fontWeight || 400};
    opacity: 1;
    text-align: left;
    box-sizing: border-box;

    &:hover {
        border-color: ${p => p.$hasError ? "#FF3B3A" : p.$disabled ? portalColors.borderStrong : "#E9EBEF"};
        outline: none;
        box-shadow: ${p => p.$disabled ? "none" : "0 0 1px #E9EBEF"};
    }
`;

const ValueArea = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex: 1 1 auto;
    overflow: ${p => p.$isMulti ? "auto hidden" : "hidden"};
    white-space: nowrap;
`;

const Placeholder = styled.span`
    color: ${portalColors.textSoft};
    font-size: ${p => p.$fontSize || "15px"};
    font-weight: ${p => p.$fontWeight || 400};
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SingleValue = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${portalColors.text};
`;

const OptionContent = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
`;

const MultiChip = styled.span`
    flex: 0 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    max-width: 12rem;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
    background: ${portalColors.surfaceTertiary};
    color: ${portalColors.text};
    font-size: 0.8125rem;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Indicator = styled.img`
    width: 14px;
    height: 14px;
    object-fit: contain;
    flex: 0 0 auto;
`;

const Menu = styled.div`
    position: absolute;
    top: calc(100% + ${p => p.$menuOffset || "0.5rem"});
    left: 0;
    z-index: 20;
    width: 100%;
    max-height: ${p => p.$maxHeight || "18rem"};
    overflow: auto;
    padding: 0.25rem;
    background: ${portalColors.surface};
    border: ${p => p.$menuBorder || ".5px solid black"};
    border-radius: ${p => p.$radius || "12px"};
    box-shadow: ${portalColors.shadow};
    box-sizing: border-box;
`;

const OptionButton = styled.button`
    width: 100%;
    min-height: 2.35rem;
    display: flex;
    align-items: center;
    justify-content: ${p => p.$isMulti ? "space-between" : "flex-start"};
    gap: 10px;
    padding: 0.55rem 0.75rem;
    border: none;
    border-radius: ${p => p.$radius || "12px"};
    background: ${p => p.$selected ? "#F0F8FF" : portalColors.surface};
    color: ${portalColors.text};
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 16px;
    text-align: left;

    &:hover {
        background: ${p => p.$isMulti ? "#E6E6FA" : portalColors.surfaceSoft};
    }
`;

const CheckInput = styled.input`
    width: 1rem;
    height: 1rem;
    transform: scale(1.5);
    cursor: pointer;
    pointer-events: none;
`;

const defaultRenderOption = (option) => option?.label ?? "";
const defaultRenderValue = (option) => option?.label ?? "";

const valuesEqual = (a, b) => String(a?.value) === String(b?.value);

function CustomSelect({
    options = [],
    value,
    defaultValue,
    onChange,
    placeholder = "Please select...",
    isDisabled = false,
    isMulti = false,
    hasError = false,
    height = "50px",
    radius = "12px",
    fontSize = "15px",
    fontWeight = 400,
    placeholderVariant = "default",
    placeholderFontWeight,
    menuOffset = "0.5rem",
    menuReserveSpace = "0",
    reserveMenuSpace = false,
    menuBorder,
    renderOption = defaultRenderOption,
    renderValue = defaultRenderValue,
    renderMultiValue = defaultRenderValue,
    showCheckboxes = false,
    className
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const selectedValue = useMemo(() => {
        if (isMulti) {
            return Array.isArray(value) ? value : [];
        }

        return value || defaultValue || null;
    }, [defaultValue, isMulti, value]);

    useEffect(() => {
        const onDocumentMouseDown = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", onDocumentMouseDown);
        return () => document.removeEventListener("mousedown", onDocumentMouseDown);
    }, []);

    const isSelected = (option) => {
        if (isMulti) {
            return selectedValue.some(item => valuesEqual(item, option));
        }
        return selectedValue ? valuesEqual(selectedValue, option) : false;
    };

    const selectOption = (option) => {
        if (isDisabled) return;

        if (isMulti) {
            const nextValue = isSelected(option)
                ? selectedValue.filter(item => !valuesEqual(item, option))
                : [...selectedValue, option];
            onChange?.(nextValue);
            return;
        }

        onChange?.(option);
        setOpen(false);
    };

    const renderButtonValue = () => {
        if (isMulti) {
            if (selectedValue.length === 0) {
                return <Placeholder $variant={placeholderVariant} $fontSize={fontSize} $fontWeight={placeholderFontWeight}>{placeholder}</Placeholder>;
            }

            return selectedValue.map(option => (
                <MultiChip key={option.value}>{renderMultiValue(option)}</MultiChip>
            ));
        }

        if (!selectedValue) {
            return <Placeholder $variant={placeholderVariant} $fontSize={fontSize} $fontWeight={placeholderFontWeight}>{placeholder}</Placeholder>;
        }

        return <SingleValue>{renderValue(selectedValue)}</SingleValue>;
    };

    return (
        <SelectWrap
            ref={rootRef}
            className={className}
            $reserveMenuSpace={open && reserveMenuSpace}
            $menuReserveSpace={menuReserveSpace}
        >
            <SelectButton
                type="button"
                onClick={() => !isDisabled && setOpen(prev => !prev)}
                disabled={isDisabled}
                $disabled={isDisabled}
                $hasError={hasError}
                $height={height}
                $radius={radius}
                $fontSize={fontSize}
                $fontWeight={fontWeight}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <ValueArea $isMulti={isMulti}>{renderButtonValue()}</ValueArea>
                <Indicator src={ArrowDownSignToNavigate} alt="" aria-hidden="true" />
            </SelectButton>

            {open && (
                <Menu role="listbox" $radius={radius} $menuOffset={menuOffset} $menuBorder={menuBorder}>
                    {options.map(option => {
                        const selected = isSelected(option);
                        return (
                            <OptionButton
                                key={option.value}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                onClick={() => selectOption(option)}
                                $selected={selected}
                                $radius={radius}
                                $isMulti={isMulti}
                            >
                                <OptionContent>{renderOption(option, { isSelected: selected, isMulti })}</OptionContent>
                                {(isMulti || showCheckboxes) && (
                                    <CheckInput type="checkbox" checked={selected} readOnly />
                                )}
                            </OptionButton>
                        );
                    })}
                </Menu>
            )}
        </SelectWrap>
    );
}

export default CustomSelect;
